import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { ApiError, api, type ChangeSetView, type FieldDiff } from '../../api/client';
import { ChangeSetDetail, ConflictList, Outcome, SourceChip, StatusChip } from '../../components/changesets';
import { useTenant } from '../../components/Shell';
import { Button, Card, Drawer, Empty, LoadError, Loading, Notice, PageHeader } from '../../components/ui';
import { actorLabel, canUndo, dateOnly, shortWhen } from '../../lib/format';
import { timesIn } from '../../lib/time';
import { useData } from '../../lib/useData';
import { useEarlier } from '../../lib/useEarlier';
// Leland's changes later, or for a while (1.12.15).
import { ScheduledCard } from '../../components/Scheduled';

export function TenantHistory() {
  const { tenant, reload: reloadTenant } = useTenant();
  const data = useData(() => api.changeSets(tenant.domain), [tenant.domain]);
  const scheduled = useData(() => api.scheduled(tenant.domain).catch(() => ({ scheduled: [] })), [tenant.domain]);
  const [params] = useSearchParams();
  // Opened from Activity: that change straight away.
  const [openId, setOpenId] = useState<string | null>(params.get('open'));
  // Older pages, loaded with "Show earlier" (kept when the list loads again by itself); and a
  // change opened by link that isn't on them.
  const earlier = useEarlier(data.data?.changeSets, data.data?.more, tenant.domain, (c) => c.createdAt);
  const { list, more } = earlier;
  const [loadingMore, setLoadingMore] = useState(false);
  const [linked, setLinked] = useState<ChangeSetView | null>(null);
  const open = list.find((c) => c.id === openId) ?? (linked?.id === openId ? linked : null);
  useEffect(() => {
    if (!openId || !data.data || list.some((c) => c.id === openId) || linked?.id === openId) return;
    api.changeSet(openId).then((r) => setLinked(r.changeSet), () => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId, data.data]);
  async function showEarlier() {
    const last = list[list.length - 1];
    if (!last) return;
    setLoadingMore(true);
    try {
      const r = await api.changeSets(tenant.domain, last.createdAt);
      earlier.addEarlier(r.changeSets, Boolean(r.more));
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <>
      <PageHeader
        eyebrow={`${tenant.domain} · ${timesIn()}`}
        title="Change history"
        actions={
          <Button
            icon="refresh"
            onClick={() => {
              data.reload();
              scheduled.reload();
            }}
          >
            Refresh
          </Button>
        }
      />
      <div className="content">
        {scheduled.data && <ScheduledCard key={scheduled.data.scheduled.map((x) => `${x.id}${x.status}`).join()} domain={tenant.domain} list={scheduled.data.scheduled} base={`/t/${tenant.domain}`} />}
        <Notice title="Every change can be undone for 30 days">
          Undo is refused if someone has changed the same settings since, so it never overwrites newer work.
        </Notice>
        <Card>
          {data.error ? (
            <LoadError error={data.error} retry={data.reload} />
          ) : !data.data ? (
            <Loading />
          ) : list.length === 0 ? (
            <Empty title="No changes yet">Changes made in Stitch show up here, ready to undo.</Empty>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">When</th>
                    <th scope="col">Change</th>
                    <th scope="col" className="hide-sm">
                      By
                    </th>
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((cs) => (
                    <tr key={cs.id} className="clickable" onClick={() => setOpenId(cs.id)}>
                      <td className="num" style={{ whiteSpace: 'nowrap' }}>
                        {shortWhen(cs.createdAt)}
                      </td>
                      <td>
                        <button type="button" className="link-btn" style={{ color: 'inherit', textAlign: 'left' }} onClick={() => setOpenId(cs.id)}>
                          {cs.title}
                        </button>{' '}
                        <SourceChip cs={cs} />
                      </td>
                      <td className="hide-sm">{actorLabel(cs)}</td>
                      <td>
                        <StatusChip status={cs.status} small />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {more && (
            <div className="card-foot center">
              <Button variant="secondary" disabled={loadingMore} onClick={() => void showEarlier()}>
                {loadingMore ? 'Loading…' : 'Show earlier'}
              </Button>
            </div>
          )}
        </Card>
      </div>
      <HistoryDrawer
        cs={open}
        onClose={() => setOpenId(null)}
        onChanged={(focusId) => {
          data.reload();
          reloadTenant();
          // The one just changed may be on an earlier page, which doesn't reload: fetch it afresh.
          if (openId) api.changeSet(openId).then((r) => earlier.replace(r.changeSet), () => undefined);
          if (focusId) setOpenId(focusId);
        }}
      />
    </>
  );
}

function HistoryDrawer({ cs, onClose, onChanged }: { cs: ChangeSetView | null; onClose: () => void; onChanged: (focusId?: string) => void }) {
  const [confirmUndo, setConfirmUndo] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ message: string; conflicts?: FieldDiff[] } | null>(null);
  const [result, setResult] = useState<ChangeSetView | null>(null);
  const [lastId, setLastId] = useState<string | null>(null);

  if (cs && cs.id !== lastId) {
    setLastId(cs.id);
    setConfirmUndo(false);
    setError(null);
    setResult(null);
  }

  async function run(action: () => Promise<ChangeSetView>, focusResult: boolean) {
    setBusy(true);
    setError(null);
    try {
      const next = await action();
      setResult(next);
      onChanged(focusResult ? next.id : undefined);
    } catch (e) {
      setError(e instanceof ApiError ? { message: e.message, conflicts: e.conflicts } : { message: String(e) });
    } finally {
      setBusy(false);
      setConfirmUndo(false);
    }
  }

  const undoable = cs ? canUndo(cs) : false;
  const done = cs?.steps.filter((s) => s.state === 'applied' && s.reversibility !== 'none') ?? [];
  const partial = cs?.steps.filter((s) => s.state === 'applied' && s.reversibility !== 'full') ?? [];

  return (
    <Drawer
      open={!!cs}
      onClose={onClose}
      title={cs?.title ?? ''}
      subtitle={cs ? `${shortWhen(cs.createdAt)} · ${actorLabel(cs)}` : ''}
      foot={
        cs && (
          <>
            {cs.status === 'planned' && (
              <>
                <Button variant="ghost" className="left" disabled={busy} onClick={() => run(async () => (await api.discard(cs.id)).changeSet, false)}>
                  Discard
                </Button>
                <Button variant="primary" disabled={busy} onClick={() => run(async () => (await api.apply(cs.id)).changeSet, false)}>
                  {busy ? 'Applying…' : 'Apply'}
                </Button>
              </>
            )}
            {undoable && !confirmUndo && (
              <Button variant="secondary" icon="undo" disabled={busy} onClick={() => setConfirmUndo(true)}>
                Undo this change
              </Button>
            )}
            {undoable && confirmUndo && (
              <>
                <Button variant="ghost" disabled={busy} onClick={() => setConfirmUndo(false)}>
                  Cancel
                </Button>
                <Button variant="primary" icon="undo" disabled={busy} onClick={() => run(async () => (await api.undo(cs.id)).undo, false)}>
                  {busy ? 'Undoing…' : 'Yes, undo it'}
                </Button>
              </>
            )}
            {!undoable && cs.status !== 'planned' && (
              <Button variant="primary" onClick={onClose}>
                Done
              </Button>
            )}
          </>
        )
      }
    >
      {cs && (
        <>
          {error && (
            <Notice tone="crit" title={error.conflicts?.length ? "Can't undo: things changed since" : "That didn't work"}>
              {error.conflicts?.length ? 'Undoing would overwrite these newer changes, so nothing was changed:' : error.message}
              {error.conflicts && error.conflicts.length > 0 && <ConflictList conflicts={error.conflicts} />}
            </Notice>
          )}
          {result && result.id !== cs.id && <Outcome cs={result} />}
          {confirmUndo && (
            <Notice tone="warn" title="Undo puts these back">
              <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                {done.map((s) => (
                  <li key={s.id}>{s.summary}</li>
                ))}
              </ul>
              {partial.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  {partial.map((s) => (
                    <div key={s.id}>{s.reversibility === 'none' ? `“${s.summary}” can't be undone.` : s.caveat}</div>
                  ))}
                </div>
              )}
            </Notice>
          )}
          <ChangeSetDetail cs={cs} />
          {cs.status === 'applied' && !undoable && cs.undoUntil && <span className="muted">Undo was available until {dateOnly(cs.undoUntil)}.</span>}
        </>
      )}
    </Drawer>
  );
}
